import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import '../styles/UserPermissions.css';
import API_BASE_URL from '../config/apiConfig.js';


const BASE_MODULES = [
    "Purchase Invoice", "Purchase Order", "Sales Invoice", "Purchase Challan", "Sales Challan", "InStock", "OutStock", "Seller", "Customer",
    "Transporter", "Product", "Category", "Expense", "ExpenseType", "Company", "User", "Grade",
    "CustomerType", "SellerType", "TransporterType", "InvoiceName", "InvoiceType", "PaymentMode", "SaleType", "Color",
    "AccountGroup", "VoucherType", "Account", "Invoice Copy", "SalesReturn", "PurchaseReturn",
    "GST Report", "Bank", "Accounting Dashboard", "E-Way Bill", "E-Invoice"
];

const SUPER_ADMIN_MODULES = [
    "Company", "User", "Plan", "Coupon"
];

const RESTRICTED_ACTIONS = {
    'Accounting Dashboard': ['add', 'update', 'delete'],
    'GST Report': ['add', 'update', 'delete']
};

const ACTIONS = [
    { key: 'add', label: 'ADD' },
    { key: 'update', label: 'UPDATE' },
    { key: 'delete', label: 'DELETE' },
    { key: 'view', label: 'VIEW' },
    { key: 'report', label: 'REPORT' },
    { key: 'show_all', label: 'SHOW ALL' }
];

const UserPermissions = ({ user, onClose }) => {
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [MODULES, setModules] = useState([]);

    const fetchPermissionsData = useCallback(async () => {
        const idToUse = user?.userId || user?._id || user?.id;
        if (!user || !idToUse) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const token = sessionStorage.getItem('token');

            // Fetch modules based on plan to filter BASE_MODULES
            const metadataResponse = await axios.get(`${API_BASE_URL}/user-permissions/metadata?userId=${idToUse}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const allowedModules = metadataResponse.data.modules || [];

            const companyName = user?.Company?.companyName ||
                user?.companyId?.companyName ||
                (user?.companyId === null || user?.company_id === null ? 'Shree Samrudh' : '');

            if (companyName === 'Shree Samrudh') {
                setModules([...SUPER_ADMIN_MODULES]);
            } else {
                // Filter BASE_MODULES by what the plan allows
                setModules(BASE_MODULES.filter(m => allowedModules.includes(m)));
            }

            // Fetch user's specific permissions
            console.log(`Fetching permissions for ${idToUse}...`);
            const response = await axios.get(`${API_BASE_URL}/user-permissions/${idToUse}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log("Permissions response data:", response.data);

            if (response.data && response.data.permissions) {
                // Ensure it's an object if it arrived as a string
                const perms = typeof response.data.permissions === 'string'
                    ? JSON.parse(response.data.permissions)
                    : response.data.permissions;

                setPermissions(perms || {});
                console.log("State 'permissions' updated with module count:", Object.keys(perms || {}).length);
            } else {
                setPermissions({});
                console.log("No specific permissions found, setting empty object.");
            }
        } catch (error) {
            console.error("Fetch permissions error:", error);
            toast.error("Failed to load permissions");
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchPermissionsData();
    }, [fetchPermissionsData]);

    const isChecked = (module, action) => {
        return permissions[module]?.[action] === 1;
    };

    const handleToggle = (module, action) => {
        setPermissions(prev => {
            const moduleData = prev[module] || {};
            const newValue = moduleData[action] === 1 ? 0 : 1;

            const updatedModule = {
                ...moduleData,
                [action]: newValue
            };

            // If 'view' is unchecked, uncheck all other permissions for this module
            if (action === 'view' && newValue === 0) {
                ACTIONS.forEach(a => {
                    updatedModule[a.key] = 0;
                });
            }
            // If any other permission is checked, automatically check 'view'
            else if (action !== 'view' && newValue === 1) {
                updatedModule['view'] = 1;
            }

            return {
                ...prev,
                [module]: updatedModule
            };
        });
    };

    const handleRowToggle = (module, checked) => {
        setPermissions(prev => {
            const newModuleData = {};
            ACTIONS.forEach(action => {
                const isRestricted = RESTRICTED_ACTIONS[module] && RESTRICTED_ACTIONS[module].includes(action.key);
                newModuleData[action.key] = isRestricted ? 0 : (checked ? 1 : 0);
            });
            return {
                ...prev,
                [module]: newModuleData
            };
        });
    };

    const handleColumnToggle = (action, checked) => {
        setPermissions(prev => {
            const updated = { ...prev };
            MODULES.forEach(module => {
                const modData = updated[module] || {};
                const isRestricted = RESTRICTED_ACTIONS[module] && RESTRICTED_ACTIONS[module].includes(action);
                const newMod = { ...modData, [action]: isRestricted ? 0 : (checked ? 1 : 0) };

                if (action === 'view' && !checked) {
                    ACTIONS.forEach(a => {
                        newMod[a.key] = 0;
                    });
                } else if (action !== 'view' && checked && !isRestricted) {
                    newMod['view'] = 1;
                }

                updated[module] = newMod;
            });
            return updated;
        });
    };

    const isColumnSelected = (action) => {
        return MODULES.every(module => permissions[module]?.[action] === 1);
    };

    const handleGlobalToggle = (checked) => {
        const newPermissions = {};
        MODULES.forEach(module => {
            newPermissions[module] = {};
            ACTIONS.forEach(action => {
                const isRestricted = RESTRICTED_ACTIONS[module] && RESTRICTED_ACTIONS[module].includes(action.key);
                newPermissions[module][action.key] = isRestricted ? 0 : (checked ? 1 : 0);
            });
        });
        setPermissions(newPermissions);
    };

    const handleSave = async () => {

        const conf = await Swal.fire({
            title: 'Save Data?',
            text: 'Are you sure you want to save this data?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#00334e',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Save',
            cancelButtonText: 'No, Keep Editing'
        });
        if (!conf.isConfirmed) return;

        const idToSave = user.userId || user._id || user.id;

        try {
            setSaving(true);
            const token = sessionStorage.getItem('token');
            if (!token) return;

            console.log("Saving permissions for ID:", idToSave, permissions);

            const response = await axios.post(`${API_BASE_URL}/user-permissions/save`, {
                userId: idToSave,
                permissions: JSON.stringify(permissions)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.status === 200) {
                await Swal.fire({
                    title: 'Saved!',
                    text: 'Permissions have been updated successfully.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });

                // If the user just edited their own permissions, notify them
                const loggedInUser = JSON.parse(sessionStorage.getItem('user'));
                const loggedInUserId = loggedInUser?.id || loggedInUser?._id || loggedInUser?.userId;

                if (String(loggedInUserId) === String(idToSave)) {
                    Swal.fire({
                        title: 'Info',
                        text: 'Refresh to apply changes to your current session.',
                        icon: 'info'
                    });
                }

                if (onClose) onClose();
            }
        } catch (error) {
            console.error("Save error:", error);
            Swal.fire({
                title: 'Error!',
                text: error.response?.data?.message || 'Failed to save permissions. Please try again.',
                icon: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    if (!user) return <div className="up-message">Please select a user first.</div>;

    const isRowSelected = (module) => {
        const applicableActions = ACTIONS.filter(a => {
            return !(RESTRICTED_ACTIONS[module] && RESTRICTED_ACTIONS[module].includes(a.key));
        });
        return applicableActions.every(action => isChecked(module, action.key));
    };

    const isGlobalSelected = MODULES.every(module => isRowSelected(module));

    if (loading) {
        return (
            <div className="up-page">
                <div className="up-loading">
                    <div className="up-spinner"></div>
                    <p>Loading permissions...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="up-page">
            <div className="up-top-bar">
                <div className="up-user-info">
                    <h1>Manage Role Permissions</h1>
                    <p>Setting access for: <strong>{user.userName}</strong> ({user.role})</p>
                </div>
                <div className="up-actions">
                    <button className="up-btn-cancel" onClick={onClose}>Cancel</button>
                    <button className="up-btn-save" onClick={handleSave} disabled={saving}>
                        {saving ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </div>

            <div className="up-table-container">
                <table className="up-main-table">
                    <thead>
                        <tr>
                            <th className="up-col-sticky">
                                <div className="up-header-cell">
                                    <input
                                        type="checkbox"
                                        className="up-styled-checkbox"
                                        checked={isGlobalSelected}
                                        onChange={(e) => handleGlobalToggle(e.target.checked)}
                                    />
                                    <span>MODULES / ACTIONS</span>
                                </div>
                            </th>
                            {ACTIONS.map(action => (
                                <th key={action.key} className="up-action-header">
                                    <div className="up-col-header-cell">
                                        <span>{action.label}</span>
                                        <input
                                            type="checkbox"
                                            className="up-styled-checkbox"
                                            checked={isColumnSelected(action.key)}
                                            onChange={(e) => handleColumnToggle(action.key, e.target.checked)}
                                        />
                                    </div>
                                </th>
                            ))}
                            <th className="up-select-all-header">SELECT ALL</th>
                        </tr>
                    </thead>
                    <tbody>
                        {MODULES.map(module => (
                            <tr key={module}>
                                <td className="up-module-name up-col-sticky">{module === 'Purchase Order' ? 'Product Order' : module}</td>
                                {ACTIONS.map(action => {
                                    const isRestricted = RESTRICTED_ACTIONS[module] && RESTRICTED_ACTIONS[module].includes(action.key);

                                    if (isRestricted) {
                                        return (
                                            <td key={`${module}-${action.key}`} className="up-checkbox-cell">
                                                <span style={{ color: '#94a3b8', fontWeight: 'bold' }}>-</span>
                                            </td>
                                        );
                                    }

                                    const hasView = isChecked(module, 'view');
                                    const isDisabled = action.key !== 'view' && !hasView;

                                    return (
                                        <td key={`${module}-${action.key}`} className="up-checkbox-cell">
                                            <label className={`up-check-label ${isDisabled ? 'disabled' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    className="up-styled-checkbox"
                                                    checked={isChecked(module, action.key)}
                                                    onChange={() => handleToggle(module, action.key)}
                                                    disabled={isDisabled}
                                                />
                                            </label>
                                        </td>
                                    );
                                })}
                                <td className="up-row-select-all">
                                    <input
                                        type="checkbox"
                                        className="up-styled-checkbox"
                                        checked={isRowSelected(module)}
                                        onChange={(e) => handleRowToggle(module, e.target.checked)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default UserPermissions;
